import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import * as roleUtil from './class/common/util/role_util';
import NOC from './class/common/const';
import { isEnabledEnv } from './class/common/util/env-util';

// -----------------------------------------------------------------------------
// 定数
// -----------------------------------------------------------------------------
// デフォルト表示画面パス
export const DEFAULT_PATH_FOR_ADMIN_OR_MANAGER = '/dashboard'; // アカウント管理者、労務管理
export const DEFAULT_PATH_FOR_GENERAL = '/labor/regist/monthly';  // 一般ユーザー

/**
 * ロールごとのデフォルトパスの取得。
 * @param role
 * @returns
 */
export function getDefaultPathForRole(role: number): string {
  if ( roleUtil.isRoleAdminOrManager(role) ) {
    return DEFAULT_PATH_FOR_ADMIN_OR_MANAGER;
  } else {
    return DEFAULT_PATH_FOR_GENERAL;
  }
}
// -----------------------------------------------------------------------------
// 閲覧権限関連
// -----------------------------------------------------------------------------
/**
 * ページ閲覧許可タイプ
 */
export const ViewPagePermission = {
  // UNDEFINED: 0, // アクセス権限未定義（ページにアクセスさせるかは、各使用箇所で判断）
  ALLOW: 1,     // 許可
  DENY: 2,      // 拒否
} as const;
// 型定義。入力値制限。
export type ViewPagePermission = typeof ViewPagePermission[keyof typeof ViewPagePermission];

/**
 * ページがlogin_user_roleの権限で閲覧可能か？
 * @param allowRoleBitFlag ページのアクセス権限ビットフラグ
 * @param loginUserRole ログイン者の権限。NOC.SETTING.ROLE.VALUE.*。未ログインの場合NOC.SETTING.ROLE.VALUE.NONEを指定する。NOC.SETTING.ROLE.VALUE.ALLは指定不可。
 * @returns ViewPagePermission ページ閲覧許可タイプ
 */
export function checkViewPagePermission(allowRoleBitFlag: number|undefined, loginUserRole: number): ViewPagePermission {
  // ページのアクセス権限ビットフラグが未設定(0 or undefined)ならログイン、未ログインにかかわらず許可。
  if ( !allowRoleBitFlag ) {
    return ViewPagePermission.ALLOW;
  }
  // console.log(` checkViewPagePermission allow_role_flag=${allow_role_bit_flag.toString(2)}b`);

  // -- ページのアクセス権限ビットフラグが未設定済み --
  // 未ログインなら拒否
  if ( loginUserRole === NOC.SETTING.ROLE.VALUE.NONE) {
    return ViewPagePermission.DENY;
  }

  // ログイン済みの場合
  // 閲覧権限があれば許可。なければ拒否。
  const loginUserRoleFlag = roleUtil.getRoleBitFlag(loginUserRole);
  if (roleUtil.isAllowRoleBitFlag(allowRoleBitFlag, loginUserRoleFlag)) {
    return ViewPagePermission.ALLOW;
  } else {
    return ViewPagePermission.DENY;
  }
}

/**
 * ページがlogin_user_roleの権限で閲覧可能か？（RouterConfig.meta版）
 * @param meta RouterConfig.meta
 * @param loginUserRole ログイン者の権限
 * @returns ViewPagePermission ページ閲覧許可タイプ
 */
export function checkViewPagePermissionMeta(meta: RouteConfigMeta|undefined, loginUserRole: number): ViewPagePermission {
  // 未定義エラー回避
  let allowRoleBitFlag;
  if ( meta && meta.allow_role_bit_flag ) {
    allowRoleBitFlag = meta.allow_role_bit_flag;
  }
  return checkViewPagePermission( allowRoleBitFlag, loginUserRole );
}

// -----------------------------------------------------------------------------
// VueRouter関連
// -----------------------------------------------------------------------------

Vue.use(VueRouter);

/**
 * RouteConfigのRemoLabo専用メタデータ。
 */
interface RouteConfigMeta {
 // 不完全契約状態（アカウント画面のみ表示できる契約状態）でアクセス可能なページ。
 is_allow_account_only_contract?: boolean;

 // ログイン後アクセス不可にするページ。
 is_deny_after_login?: boolean;

 /**
  * アクセス許可ロールビットフラグ。ROLE_FLAG_*を指定。
  * undefined or 0: アクセス制限なし、未ログインでもアクセス可能。
  * ROLE_FLAG_ALL: 何かしらの権限でログインしていればアクセス可能。
  */
 allow_role_bit_flag?: number;
}

/**
 * VueRouter設定
 * @field meta RouteConfigMetaを参照。
 */
export const routes: RouteConfig[] = [
  // '/' はロールに応じて遷移先を変更する。一般ユーザーはダッシュボードにアクセスできないため。App.vue calcTransitionPath() で処理。
  // {
  //   path: '/',
  //   name: 'Dashboard',
  //   component: () => import('@/views/Dashboard.vue'),
  // },
  {
    path: '/dashboard',
    component: () => import('@/views/Dashboard.vue'),
    meta: {
      // note takezawa ダッシュボードの閲覧権限は環境変数によって変化するため App.vue calcTransitionPath() で処理している。ここではいちおうデフォルト設定をしておく。
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  // 就業形態管理
  {
    path: '/setting/work_style',
    name: 'Setting-Workstyle',
    component: () => import('@/views/setting/Workstyle.vue'),
    meta: {
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN,
    },
  },
  // ユーザー管理
  {
    path: '/setting/users',
    name: 'Setting-Users',
    component: () => import('@/views/setting/Users.vue'),
    meta: {
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN,
    },
  },
  // 業務管理
  {
    path: '/setting/work_manage',
    name: 'Setting-Workmanage',
    component: () => import('@/views/setting/Workmanage.vue'),
    meta: {
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  // グループ管理
  {
    path: '/setting/group',
    name: 'Setting-Group',
    component: () => import('@/views/setting/Group.vue'),
    meta: {
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  // 通知設定
  {
    path: '/setting/alert',
    name: 'Setting-Alert',
    component: () => import('@/views/setting/Alert.vue'),
    meta: {
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  // 操作回数管理
  {
    path: '/setting/operation_count',
    name: 'Setting-Operationcount',
    component: () => import('@/views/setting/OperationCount.vue'),
    meta: {
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN,
    },
  },
  {
    path: '/account',
    name: 'Account',
    component: () => import('@/views/Account.vue'),
    redirect: '/license',
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('@/views/Contact.vue'),
    meta: {
      is_allow_account_only_contract: true,
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  {
    path: '/help/uninstall',
    name: 'UninstallHelp',
    component: () => import('@/views/UninstallHelp.vue'),
    meta: {
      is_allow_account_only_contract: true,
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  {
    path: '/help/:article?', // '/help' と '/help/foo' のどちらも許可
    name: 'Help',
    component: () => import('@/views/Help.vue'),
    props: true,
    meta: {
      is_allow_account_only_contract: true,
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  {
    path: '/news',
    name: 'News',
    component: () => import('@/views/News.vue'),
    meta: {
      is_allow_account_only_contract: true,
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  {
    path: '/news/:news_id',
    component: () => import('@/views/News.vue'),
    meta: {
      is_allow_account_only_contract: true,
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  // アラート通知画面
  {
    path: '/alert',
    name: 'AlertList',
    component: () => import('@/views/AlertList.vue'),
    meta: {
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  // {
  //   path: '/report/monthly',
  //   name: 'MonthlyReport',
  //   component: () => import('@/views/MonthlyReport.vue'),
  // },
  {
    path: '/Personal',
    name: 'Personal',
    component: () => import('@/parts/app/disp/Personal.vue'),
  },
  {
    path: '/Signup',
    meta: {
      is_deny_after_login: true,
    },
  },
  {
    path: '/Login',
    meta: {
      is_deny_after_login: true,
    },
  },
  {
    path: '/new',
  },
  {
    path: '/Invoice',
    name: 'Invoice',
    component: () => import('@/parts/app/disp/Invoice.vue'),
    meta: {
      is_allow_account_only_contract: true,
    },
  },
  /*
  { // 追加
    path: '/error',
    name: 'サイトエラー',
    component: () => import('@/views/common/Error.vue'),
  },
  */
  // ライセンス・お支払い情報
  {
    path: '/license',
    name: 'License',
    component: () => import('@/views/License.vue'),
    meta: {
      is_allow_account_only_contract: true,
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN,
    },
  },
  // クライアントツール
  {
    path: '/clientTools',
    name: 'ClientTools',
    component: () => import('@/views/ClientTools.vue'),
    meta: {
      is_allow_account_only_contract: true,
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN,
    },
  },
  // ご契約者情報
  {
    path: '/subscriberInfo',
    name: 'SubscriberInfo',
    component: () => import('@/views/SubscriberInfo.vue'),
    meta: {
      is_allow_account_only_contract: true,
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN,
    },
  },
  {
    path: '/service/introduction',
    name: 'ServiceIntroduction',
    component: () => import('@/service/Introduction.vue'),
  },
  {
    path: '/service/contact',
    name: 'ServiceContact',
    component: () => import('@/service/Contact.vue'),
  },
  {
    path: '/service/:catchAll(.*)',
    component: () => import('@/parts/app/disp/Welcome.vue'),
  },
  {
    path: '/debug',
    name: 'DebugHome',
    component: () => import('@/views/debug/DebugHome.vue'),
    props: true,
    beforeEnter: async (to, from, next) => {
      if ( isEnabledEnv( process.env.VUE_APP_USE_DEBUG )) {
        next({name: 'Error_404'});
      } else {
        next();
      }
    },
  },
  {
    path: '/debug/log_result_panel',
    name: 'DebugLogResultPanel',
    component: () => import('@/views/debug/DebugLogResultPanel.vue'),
    props: true,
    beforeEnter: async (to, from, next) => {
      if ( isEnabledEnv( process.env.VUE_APP_USE_DEBUG ) ) {
        next({name: 'Error_404'});
      } else {
        next();
      }
    },
  },
  // 勤怠登録_月
  {
    path: '/labor/regist/monthly',
    name: 'RegistMonthly',
    component: () => import('@/views/labor/LaborRegistMonthly.vue'),
    meta: {
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ALL,
    },
  },
  // 勤怠登録_日
  {
    path: '/labor/regist/daily',
    name: 'RegistDaily',
    component: () => import('@/views/labor/LaborRegistDaily.vue'),
    meta: {
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ALL,
    },
  },
  // 勤怠管理_月
  {
    path: '/labor/management/monthly',
    name: 'ManagementMonthly',
    component: () => import('@/views/labor/LaborManagementMonthly.vue'),
    meta: {
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  // 勤怠管理_日
  {
    path: '/labor/management/daily',
    name: 'ManagementDaily',
    component: () => import('@/views/labor/LaborManagementDaily.vue'),
    meta: {
      allow_role_bit_flag: roleUtil.ROLE_FLAG_ADMIN_OR_MANAGER,
    },
  },
  {
    path: '/:catchAll(.*)',
    name: 'Error_404',
    // 存在しないURLの場合、エラー画面（404）に遷移させるため
    // redirect: '/',
    component: () => import('@/views/Error_404.vue'),
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

export default router;

/**
 * RouterConfigメタデータの検索。
 * @param path
 * @returns
 */
export function findRouterConfig(path: string): RouteConfig | undefined {
  // console.log(`findRouterConfig path=${path}`);
  const pathLower = path.toLowerCase();  // 大文字小文字を区別しない。VueRouterの仕様と同じにする。
  const foundRouteConfig = routes.find( routeInfo => {
    // : パラメータ以降を除外
    const splitPathList = routeInfo.path.split('/:');
    const routePathWithoutParam = splitPathList[0].toLowerCase();  // 大文字小文字を区別しない。VueRouterの仕様と同じにする。
    if ( splitPathList.length >= 2 ) {
      // パラメータ付きパスの場合は、前方一致で、合致とみなす。
      return pathLower.startsWith(routePathWithoutParam);
    } else {
      // 通常は完全一致で合致とみなす。
      return pathLower === routePathWithoutParam;
    }
  });
  // console.log(`findRouterConfig foundRouteConfig=${JSON.stringify(foundRouteConfig)}`);
  return foundRouteConfig;
}

/**
 * RouterConfigメタデータの検索。
 * @param path
 * @returns
 */
export function findRouterConfigMetaData(path: string): RouteConfigMeta | undefined {
  const foundRouteConfig = findRouterConfig(path);
  if ( !foundRouteConfig ) {
    return undefined;
  }
  if ( !foundRouteConfig.meta ) {
    return undefined;
  }
  return foundRouteConfig.meta;
}
